// Métricas de trafficker — bloque `ads` de GET /admin/public-tracking/campaign.
// Entrega de Meta (alcance, impresiones, frecuencia, clics, CTR, CPC, CPM, gasto) unida
// al embudo propio por utm_campaign / utm_content → costo por visita, por conversación,
// por registro (CPL), por pago (CAC) y ROAS. Sin conexión: pasos para conectar, CERO
// números inventados. Tabla ordenable. Tokens semánticos.
import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Megaphone, ArrowUpDown, Link2 } from 'lucide-react';
import type { AdCampaignRow, AdRow, CampaignAds, OwnUtmRow } from '@/modules/public-tracking/api';

const nf = new Intl.NumberFormat('es-MX');
const n0 = (v: number | null | undefined) => (v === null || v === undefined ? '—' : nf.format(Math.round(v)));
const n2 = (v: number | null | undefined, suf = '') =>
  v === null || v === undefined ? '—' : `${v.toLocaleString('es-MX', { maximumFractionDigits: 2, minimumFractionDigits: v < 10 ? 2 : 0 })}${suf}`;
function moneyFmt(cur: string) {
  let f: Intl.NumberFormat;
  try {
    f = new Intl.NumberFormat('es-MX', { style: 'currency', currency: cur || 'MXN', maximumFractionDigits: 2 });
  } catch {
    f = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 });
  }
  return (v: number | null | undefined) => (v === null || v === undefined ? '—' : f.format(v));
}

type Row = AdCampaignRow | AdRow;
type Col = { key: string; label: string; get: (r: Row) => number | null; fmt: 'n0' | 'n2' | 'pct' | 'money' | 'x'; title?: string };
const COLS: Col[] = [
  { key: 'reach', label: 'Alcance', get: (r) => r.reach, fmt: 'n0' },
  { key: 'impressions', label: 'Impr.', get: (r) => r.impressions, fmt: 'n0', title: 'Impresiones' },
  { key: 'frequency', label: 'Frec.', get: (r) => r.frequency, fmt: 'n2', title: 'Frecuencia = impresiones ÷ alcance' },
  { key: 'clicks', label: 'Clics', get: (r) => r.clicks, fmt: 'n0' },
  { key: 'ctr', label: 'CTR', get: (r) => r.ctr, fmt: 'pct' },
  { key: 'cpc', label: 'CPC', get: (r) => r.cpc, fmt: 'money', title: 'Costo por clic' },
  { key: 'cpm', label: 'CPM', get: (r) => r.cpm, fmt: 'money', title: 'Costo por mil impresiones' },
  { key: 'spend', label: 'Gasto', get: (r) => r.spend, fmt: 'money' },
  { key: 'visitors', label: 'Visitas', get: (r) => r.own.visitors, fmt: 'n0', title: 'Visitantes medidos en el sitio con esta utm' },
  { key: 'conversations', label: 'Conv.', get: (r) => r.own.conversations, fmt: 'n0', title: 'Conversaciones desde el sitio (WhatsApp o chat)' },
  { key: 'registrations', label: 'Registros', get: (r) => r.own.registrations, fmt: 'n0' },
  { key: 'payments', label: 'Pagos', get: (r) => r.own.payments, fmt: 'n0' },
  { key: 'cost_per_visit', label: '$/visita', get: (r) => r.cost_per_visit, fmt: 'money' },
  { key: 'cost_per_conversation', label: '$/conv.', get: (r) => r.cost_per_conversation, fmt: 'money' },
  { key: 'cpl', label: 'CPL', get: (r) => r.cpl, fmt: 'money', title: 'Costo por registro' },
  { key: 'cac', label: 'CAC', get: (r) => r.cac, fmt: 'money', title: 'Costo por pago' },
  { key: 'roas', label: 'ROAS', get: (r) => r.roas, fmt: 'x', title: 'Ingreso de pagos ÷ gasto' },
];

const STEPS = [
  'En Meta Business Suite, comparte la cuenta publicitaria, la página de Facebook y la cuenta de Instagram con el negocio que administra Orión (o pide a soporte que lo hagan).',
  'Soporte asigna esos activos a tu organización (cuenta publicitaria act_…, página e Instagram) y guarda el token del usuario de sistema en el almacén cifrado de tu organización — nunca por chat ni en el código.',
  'En las URLs de tus anuncios usa: utm_source=facebook&utm_medium=paid&utm_campaign={{campaign.name}}&utm_content={{ad.name}}. Así cada visita, registro y pago se une a su campaña y anuncio.',
];

export const AdsPanel: React.FC<{ ads: CampaignAds | null | undefined; onBuildLink?: () => void }> = ({ ads, onBuildLink }) => {
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 }>({ key: 'spend', dir: -1 });
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const rows = useMemo(() => {
    if (!ads || !ads.connected) return [];
    const col = COLS.find((c) => c.key === sort.key) || COLS[7];
    const val = (r: Row) => col.get(r) ?? -Infinity;
    const byKey = (a: Row, b: Row) => (val(a) - val(b)) * sort.dir;
    return [...ads.campaigns].sort(byKey).map((c) => ({ ...c, ads: [...c.ads].sort(byKey) }));
  }, [ads, sort]);

  if (ads === undefined || ads === null) return null;

  if (!ads.connected) {
    return (
      <Card>
        <h3 className="font-semibold flex items-center gap-2"><Megaphone className="h-4 w-4 text-primary" />Anuncios (trafficker)</h3>
        <div className="mt-3 rounded-lg border border-dashed border-border p-4">
          <p className="font-medium">Conecta tu cuenta de Meta</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Sin la cuenta publicitaria no mostramos alcance, gasto ni costos: preferimos no inventar números.
          </p>
          <ol className="mt-3 list-decimal pl-5 space-y-1.5 text-sm text-muted-foreground">
            {STEPS.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
          {onBuildLink && (
            <button type="button" onClick={onBuildLink} className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
              <Link2 className="h-4 w-4" /> Armar una liga etiquetada
            </button>
          )}
        </div>
        {!!ads.own_by_utm?.length && <OwnTable rows={ads.own_by_utm} title="Tu embudo por liga etiquetada (sin costos)" />}
      </Card>
    );
  }

  const money = moneyFmt(ads.currency);
  const fmt = (c: Col, v: number | null) =>
    c.fmt === 'n0' ? n0(v) : c.fmt === 'n2' ? n2(v) : c.fmt === 'pct' ? n2(v, ' %') : c.fmt === 'x' ? n2(v, '×') : money(v);
  const t = ads.totals;

  return (
    <Card>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-semibold flex items-center gap-2"><Megaphone className="h-4 w-4 text-primary" />Anuncios (trafficker)</h3>
        <span className="text-xs text-muted-foreground">Meta · {ads.ad_account_id} · {ads.period.since} a {ads.period.until}{ads.currency ? ` · ${ads.currency}` : ''}</span>
      </div>
      {ads.error && (
        <div className="mt-3 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          Meta no respondió ({ads.error.slice(0, 160)}). No mostramos números hasta que responda.
        </div>
      )}
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-2 text-sm">
        {[
          ['Gasto', money(t.spend)], ['Impresiones', n0(t.impressions)], ['Clics', n0(t.clicks)], ['CTR', n2(t.ctr, ' %')],
          ['CPC', money(t.cpc)], ['CPL', money(t.cpl)], ['CAC', money(t.cac)], ['ROAS', ads.roas_comparable ? n2(t.roas, '×') : 'otra moneda'],
        ].map(([k, v]) => (
          <div key={k} className="rounded-lg border border-border p-2 min-w-0">
            <div className="text-[11px] text-muted-foreground">{k}</div>
            <div className="font-semibold tabular-nums truncate">{v}</div>
          </div>
        ))}
      </div>
      {rows.length ? (
        <div className="mt-3 -mx-4 md:mx-0 overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="py-2 px-4 md:pl-0 font-medium">Campaña / anuncio</th>
                {COLS.map((c) => (
                  <th key={c.key} className="py-2 px-1.5 font-medium text-right whitespace-nowrap" title={c.title}>
                    <button type="button" className={`inline-flex items-center gap-0.5 hover:text-foreground ${sort.key === c.key ? 'text-foreground' : ''}`}
                      onClick={() => setSort((s) => ({ key: c.key, dir: s.key === c.key ? (s.dir === 1 ? -1 : 1) : -1 }))}
                      aria-label={`Ordenar por ${c.title || c.label}`}>
                      {c.label}<ArrowUpDown className="h-3 w-3 opacity-60" />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <React.Fragment key={c.campaign_id}>
                  <tr className="border-b border-border/60">
                    <td className="py-2 px-4 md:pl-0">
                      <button type="button" className="flex items-center gap-1 text-left text-foreground font-medium" onClick={() => setOpen((o) => ({ ...o, [c.campaign_id]: !o[c.campaign_id] }))}>
                        {c.ads.length ? (open[c.campaign_id] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />) : <span className="w-3.5" />}
                        <span className="truncate max-w-[220px]" title={c.campaign_name}>{c.campaign_name}</span>
                      </button>
                    </td>
                    {COLS.map((col) => <td key={col.key} className="py-2 px-1.5 text-right tabular-nums">{fmt(col, col.get(c))}</td>)}
                  </tr>
                  {open[c.campaign_id] && c.ads.map((a) => (
                    <tr key={a.ad_id} className="border-b border-border/40 bg-muted/30">
                      <td className="py-1.5 px-4 md:pl-6 text-xs text-muted-foreground truncate max-w-[240px]" title={`${a.adset_name} · ${a.ad_name}`}>{a.ad_name}</td>
                      {COLS.map((col) => <td key={col.key} className="py-1.5 px-1.5 text-right tabular-nums text-xs">{fmt(col, col.get(a))}</td>)}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          <p className="mt-2 px-4 md:px-0 text-xs text-muted-foreground">
            Visitas, conversaciones, registros y pagos se unen por utm_campaign = nombre (o id) de la campaña y utm_content = nombre del anuncio.
            {!ads.roas_comparable && ' El ROAS no se calcula: los pagos están en otra moneda que la cuenta publicitaria.'}
          </p>
        </div>
      ) : (
        !ads.error && <p className="mt-3 text-sm text-muted-foreground">Sin campañas con entrega en este periodo.</p>
      )}
      {!!ads.unmatched_own?.length && <OwnTable rows={ads.unmatched_own} title="Ligas etiquetadas sin campaña de Meta (orgánico, WhatsApp, correo…)" />}
      {ads.organic && (ads.organic.facebook.length + ads.organic.instagram.length > 0) && (
        <div className="mt-4">
          <h4 className="text-sm font-semibold">Publicaciones orgánicas del periodo</h4>
          <p className="text-xs text-muted-foreground">Interacciones (me gusta + comentarios + compartidos). El alcance por publicación aún no lo trae el lector.</p>
          <ul className="mt-2 grid gap-2 sm:grid-cols-2">
            {[...ads.organic.instagram.map((p) => ({ ...p, net: 'Instagram' })), ...ads.organic.facebook.map((p) => ({ ...p, net: 'Facebook' }))]
              .sort((a, b) => b.interactions - a.interactions).slice(0, 8).map((p) => (
                <li key={p.id} className="rounded-lg border border-border p-2 text-xs flex justify-between gap-2">
                  <span className="truncate"><span className="font-medium">{p.net}</span> · {p.caption || p.type || p.id}</span>
                  <span className="tabular-nums shrink-0">{n0(p.interactions)}</span>
                </li>
              ))}
          </ul>
        </div>
      )}
    </Card>
  );
};

const OwnTable: React.FC<{ rows: OwnUtmRow[]; title: string }> = ({ rows, title }) => (
  <div className="mt-4">
    <h4 className="text-sm font-semibold">{title}</h4>
    <div className="mt-2 -mx-4 md:mx-0 overflow-x-auto">
      <table className="w-full text-sm min-w-[560px]">
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b border-border">
            <th className="py-1.5 px-4 md:pl-0 font-medium">utm_campaign · utm_content</th>
            <th className="py-1.5 px-2 font-medium text-right">Visitas</th>
            <th className="py-1.5 px-2 font-medium text-right">Conv.</th>
            <th className="py-1.5 px-2 font-medium text-right">Registros</th>
            <th className="py-1.5 px-4 md:pr-0 font-medium text-right">Pagos</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 20).map((r, i) => (
            <tr key={i} className="border-b border-border/60 last:border-0">
              <td className="py-1.5 px-4 md:pl-0 truncate max-w-[280px]">{r.utm_campaign}{r.utm_content ? <span className="text-muted-foreground"> · {r.utm_content}</span> : null}</td>
              <td className="py-1.5 px-2 text-right tabular-nums">{n0(r.visitors)}</td>
              <td className="py-1.5 px-2 text-right tabular-nums">{n0(r.conversations)}</td>
              <td className="py-1.5 px-2 text-right tabular-nums">{n0(r.registrations)}</td>
              <td className="py-1.5 px-4 md:pr-0 text-right tabular-nums">{n0(r.payments)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-xl border border-border bg-card text-card-foreground p-4 md:p-5 min-w-0">{children}</div>
);

export default AdsPanel;
